import { IconType, IconVariant } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import {
  PaymentServicePaymentBatchStatus as PaymentBatchStatusEnum,
  PaymentMethod as PaymentMethodEnum,
} from "#graphql/supergraphEnums";
import { PaymentServicePaymentBatchStatus, PaymentMethod } from "#graphql/supergraphTypes";

/**
 * UI configuration for batch status display
 */
export type BatchStatusUIConfig = {
  label: string;
  labelColor: Colors;
  iconType: IconType;
  iconColor: Colors;
  iconVariant?: IconVariant;
};

/**
 * Human-readable labels for batch statuses
 */
export enum BatchStatusLabel {
  ReadyForReview = "Ready for review",
  Approved = "Approved",
  FundingInProgress = "Funding in progress",
  Funded = "Funded",
  WaitingForClientAuthorization = "Waiting for client authorization",
  Scheduled = "Scheduled",
  Processing = "Processing",
  Transmitted = "Transmitted",
  Failed = "Failed",
  Canceled = "Canceled",
  ApprovalDenied = "Approval denied",
  Draft = "Draft",
}

/**
 * Maps a PaymentServicePaymentBatchStatus to UI configuration with label, icon, and color
 * @param status - The payment batch status
 * @param isGPX - Whether this is a GPX client (affects ReadyForReview label)
 * @param method - The payment method (affects FundingInProgress and Funded labels)
 */
export const batchStatusUIConfig = (
  status: PaymentServicePaymentBatchStatus,
  isGPX?: boolean,
  method?: PaymentMethod,
): BatchStatusUIConfig => {
  let label: string;
  let iconType: IconType;
  let iconColor: Colors;
  let iconVariant: IconVariant | undefined;

  switch (status) {
    case PaymentBatchStatusEnum.ReadyForTreasuryReview:
      label = BatchStatusLabel.ReadyForReview;
      iconType = "user-magnifying-glass";
      iconColor = Colors.Warning600;
      iconVariant = "solid";
      break;
    case PaymentBatchStatusEnum.Approved:
      label = BatchStatusLabel.Approved;
      iconType = "check";
      iconColor = Colors.Success600;
      iconVariant = "solid";
      break;
    case PaymentBatchStatusEnum.FundingInProgress:
      // For ACH payments, show "Processing" instead of "Funding in progress"
      if (method === PaymentMethodEnum.Ach) {
        label = BatchStatusLabel.Processing;
        iconType = "arrows-rotate";
        iconColor = Colors.Neutral800;
        iconVariant = "solid";
      } else {
        label = BatchStatusLabel.FundingInProgress;
        iconType = "arrows-rotate";
        iconColor = Colors.Neutral800;
        iconVariant = "solid";
      }
      break;
    case PaymentBatchStatusEnum.Funded:
      // For ACH payments, show "Processing" instead of "Funded"
      if (method === PaymentMethodEnum.Ach) {
        label = BatchStatusLabel.Processing;
        iconType = "arrows-rotate";
        iconColor = Colors.Neutral800;
        iconVariant = "solid";
      } else {
        label = BatchStatusLabel.Funded;
        iconType = "coins";
        iconColor = Colors.Success600;
      }
      break;
    case PaymentBatchStatusEnum.ReadyForReview:
      label = isGPX
        ? BatchStatusLabel.ReadyForReview
        : BatchStatusLabel.WaitingForClientAuthorization;
      iconType = isGPX ? "user-magnifying-glass" : "stamp";
      iconColor = isGPX ? Colors.Warning600 : Colors.Neutral800;
      iconVariant = isGPX ? "solid" : undefined;
      break;
    case PaymentBatchStatusEnum.TransmittingInProgress:
      label = BatchStatusLabel.Scheduled;
      iconType = "circle-notch";
      iconColor = Colors.Neutral800;
      iconVariant = "solid";
      break;
    case PaymentBatchStatusEnum.Transmitted:
      label = BatchStatusLabel.Transmitted;
      iconType = "circle-check";
      iconColor = Colors.Success600;
      iconVariant = "solid";
      break;
    case PaymentBatchStatusEnum.Failed:
      label = BatchStatusLabel.Failed;
      iconType = "exclamation-circle";
      iconColor = Colors.Danger600;
      break;
    case PaymentBatchStatusEnum.Canceled:
      label = BatchStatusLabel.Canceled;
      iconType = "ban";
      iconColor = Colors.Neutral800;
      iconVariant = "solid";
      break;
    case PaymentBatchStatusEnum.Denied:
    case PaymentBatchStatusEnum.TreasuryDenied:
      label = BatchStatusLabel.ApprovalDenied;
      iconType = "xmark-large";
      iconColor = Colors.Danger600;
      iconVariant = "solid";
      break;
    case PaymentBatchStatusEnum.Draft:
    default:
      label = BatchStatusLabel.Draft;
      iconType = "file";
      iconColor = Colors.Neutral800;
      break;
  }

  return {
    label,
    labelColor: iconColor,
    iconType,
    iconColor,
    iconVariant,
  };
};
